import type { ArticleBody } from './types';

const NHS = (path: string, label: string) => ({ label: `NHS, ${label}`, href: `https://www.nhs.uk/${path}` });
const AAP = (path: string, label: string) => ({ label: `American Academy of Pediatrics (HealthyChildren.org) ${label}`, href: `https://www.healthychildren.org/English/${path}` });
const RBSK = { label: 'Rashtriya Bal Swasthya Karyakram (Government of India)', href: 'https://rbsk.mohfw.gov.in/' };

export const CONDITIONS: Record<string, ArticleBody> = {
  autism: {
    intro: [
      'Autism is a lifelong difference in how a person communicates, relates to others and experiences the world. It is called a **spectrum** because it looks very different from one child to the next: some children talk a lot and some use few or no words; some need a lot of support and some need a little.',
      'Autism is not caused by parenting, by screens or by vaccines. Children are born autistic; the signs usually become clearer in the first few years.',
    ],
    notice: [
      'Not answering to their name by around the first birthday, even though hearing is fine.',
      'Few gestures such as pointing, waving or showing you things; less back-and-forth smiling or sharing.',
      'Talking late, losing words they used to say, or repeating phrases from videos or other people.',
      'Playing in particular ways, lining up toys, spinning wheels, watching things move, and finding it hard to be interrupted.',
      'Strong reactions to sounds, textures, lights or food, or seeming not to notice pain or cold.',
      'Big distress at small changes in routine, routes or rules.',
      'Many children are also affectionate, funny and deeply interested in the things they love. Both are true.',
    ],
    helps: [
      'Follow their lead in play. Join what they are already doing and add one small new thing.',
      'Use fewer words and more showing: point, use gestures, pictures and a visual timetable for the day.',
      'Keep routines predictable and warn before changes (“Two more minutes, then bath”).',
      'Notice what their senses need, a quiet corner, headphones, a firm hug, time to move, and plan for it.',
      'Celebrate every way they communicate, including gestures, pictures and devices. They do not stop speech from coming; they often help it.',
    ],
    myths: [
      { myth: 'Two languages at home confuse children and cause autism or late talking.', fact: 'Bilingual homes do not cause autism or delay. Speak the language you are most comfortable in.' },
      { myth: 'Autism is caused by too much TV or by cold parenting.', fact: 'Neither causes autism. Autism is a difference in how the brain develops.' },
      { myth: 'Autistic children don’t feel love or want friends.', fact: 'They love and connect deeply, sometimes in ways that look different from what we expect.' },
    ],
    when: [
      'No babbling or pointing by 12 months, no single words by 16 months, or no two-word phrases by 24 months.',
      'Any loss of words or social skills your child used to have, at any age.',
      'You, a teacher or a relative keep having the feeling that something is different. That feeling is worth checking.',
    ],
    who: ['Developmental paediatrician', 'Child psychologist', 'Speech and language therapist', 'Occupational therapist'],
    nurture:
      'The home assessment looks closely at connecting and communicating, and the report explains in plain words what stood out, with a page you can hand to the doctor. It cannot diagnose autism; it helps you ask the right questions sooner.',
    sources: [
      { label: 'World Health Organization; Autism', href: 'https://www.who.int/news-room/fact-sheets/detail/autism-spectrum-disorders' },
      NHS('conditions/autism/', 'Autism'),
      AAP('health-issues/conditions/Autism/Pages/default.aspx', 'Autism'),
    ],
  },

  adhd: {
    intro: [
      'ADHD (attention deficit hyperactivity disorder) is a difference in how the brain manages attention, activity and impulses. Every child is sometimes restless or distracted; with ADHD it happens far more than for other children of the same age, in more than one place, and it gets in the way of everyday life.',
      'ADHD is not caused by poor parenting or sugar. It often runs in families.',
    ],
    notice: [
      'Finding it very hard to sit through meals, lessons or stories, even ones they enjoy.',
      'Starting many things and finishing few; losing things; forgetting instructions part-way through.',
      'Acting before thinking: grabbing, interrupting, running into the road.',
      'Being able to focus for hours on a game they love, but not on homework. This is typical of ADHD, not a sign it is absent.',
      'Big feelings that flare quickly and settle quickly.',
      'In some children, especially girls, ADHD is mostly daydreaming and quiet disorganisation, and is easily missed.',
    ],
    helps: [
      'Give one instruction at a time, with your child looking at you, and ask them to say it back.',
      'Break tasks into small steps with a tick list; use timers for short bursts of work with movement breaks.',
      'Catch them being good. Specific praise (“You put your shoes away first time!”) works better than correction.',
      'Keep mornings and evenings the same every day. Prepare the night before.',
      'Plenty of outdoor play, regular sleep and meals help attention more than any single food or supplement.',
    ],
    myths: [
      { myth: 'They can concentrate on the phone, so it can’t be ADHD.', fact: 'Children with ADHD can hyper-focus on things they find exciting. The difficulty is with tasks that are not.' },
      { myth: 'ADHD is just naughtiness and needs stricter discipline.', fact: 'Harsh punishment usually makes things worse. Clear routines and warm, consistent responses help.' },
    ],
    when: [
      'Difficulties show up both at home and at school (or with other caregivers), for six months or more.',
      'Teachers are raising the same concerns you see at home.',
      'Your child is often hurt, left out, or upset with themselves because of it.',
    ],
    who: ['Developmental paediatrician', 'Child psychiatrist', 'Child psychologist'],
    nurture:
      'For children aged about 3 and up, the home assessment asks about attention and activity at home. The report shows whether this area needs attention or is one to keep monitoring, with ideas for home and a page for your child’s doctor.',
    sources: [NHS('conditions/attention-deficit-hyperactivity-disorder-adhd/', 'ADHD'), AAP('health-issues/conditions/adhd/Pages/default.aspx', 'ADHD')],
  },

  'learning-difficulties': {
    intro: [
      'A specific learning difficulty means a child finds one area of learning, most often reading and spelling (**dyslexia**), handwriting (**dysgraphia**) or maths (**dyscalculia**) much harder than you would expect from how bright and capable they are otherwise.',
      'It is not about intelligence or effort. With the right teaching and support, children with learning difficulties do learn, and many do very well.',
    ],
    notice: [
      'Still mixing up letters and sounds, or guessing words from pictures, well after classmates have moved on.',
      'Reading slowly and tiring quickly; understanding a story read aloud much better than one they read themselves.',
      'Spelling the same word differently on one page; very messy or painful handwriting.',
      'Trouble remembering times tables, telling the time or handling money.',
      'Hating homework, saying “I’m stupid”, or tummy aches before school.',
    ],
    helps: [
      'Read aloud together every day for pleasure, not testing. Audiobooks count.',
      'Keep practice short, daily and fun: magnetic letters, rhymes, board games with dice and counting.',
      'Praise effort and strategy, not just marks.',
      'Talk to the class teacher early and ask what support they can offer in class.',
    ],
    myths: [
      { myth: 'They are lazy. They just need to try harder.', fact: 'Children with learning difficulties are often working harder than anyone else in the class.' },
      { myth: 'They will grow out of it.', fact: 'The difficulty stays, but good teaching and support make a big difference, and earlier is better.' },
    ],
    when: [
      'Reading, writing or maths is clearly behind after a year or more of school, despite help.',
      'Your child is becoming anxious about school or losing confidence.',
    ],
    who: ['Special educator', 'Child psychologist (for a learning assessment)', 'Occupational therapist (for handwriting)', 'Developmental paediatrician'],
    nurture:
      'Nurture looks at everyday development rather than school subjects, so it does not test reading or maths. It can show whether attention, language or other areas are also worth a look; useful to share when you ask for a learning assessment.',
    sources: [NHS('conditions/dyslexia/', 'Dyslexia'), AAP('health-issues/conditions/learning-disabilities/Pages/default.aspx', 'Learning disabilities')],
  },

  'speech-language-delay': {
    intro: [
      'A speech and language delay means a child is understanding or using words later than most children of the same age. **Speech** is making sounds clearly; **language** is understanding and putting words together to share ideas. A child can have difficulty with one or both.',
      'Some children catch up on their own. Others need help, and it is hard to tell which from the outside, so it is worth checking rather than waiting.',
    ],
    notice: [
      'Few words by 18 months, or not joining two words together by 2 years.',
      'Pointing, pulling you or crying to ask for things, instead of trying words.',
      'Not following simple instructions like “bring your shoes” without gestures.',
      'Speech that family members outside the home cannot understand by around age 3.',
      'Frustration, tantrums or giving up when they are not understood.',
    ],
    helps: [
      'Talk about what your child is doing, as they do it: “You’re pouring. Pour, pour!”',
      'Add one word to what they say: if they say “car”, you say “red car” or “car go”.',
      'Get down to their eye level and wait, count to five in your head, to give them a turn.',
      'Sing rhymes with actions, read picture books, and turn screens off in the background.',
      'Speak your home language; it is the richest language you have to share.',
    ],
    myths: [
      { myth: 'Boys always talk late; wait and see.', fact: 'Boys may talk a little later on average, but a clear delay is worth checking in any child.' },
      { myth: 'Two languages at home cause late talking.', fact: 'They don’t. Bilingual children reach milestones in the usual range across their languages.' },
    ],
    when: [
      'No words by 16 months, no two-word phrases by 2 years, or losing words at any age.',
      'Your child doesn’t seem to understand simple things said to them; have their hearing checked too.',
    ],
    who: ['Speech and language therapist', 'Audiologist (hearing test)', 'Developmental paediatrician'],
    nurture:
      'The home assessment asks about everyday communication for your child’s age, and the report shows where talking and understanding sit, with activities to try at home and a page for a speech therapist or doctor.',
    sources: [
      { label: 'ASHA; Speech and language development', href: 'https://www.asha.org/public/speech/development/' },
      AAP('ages-stages/toddler/Pages/Language-Delay.aspx', 'Language delay'),
    ],
  },

  'developmental-delay': {
    intro: [
      '**Developmental delay** means a young child is reaching several milestones, sitting, walking, talking, playing, feeding themselves, later than most children. It describes where a child is now, not what they will become.',
      '**Intellectual disability** is a term used when learning and everyday skills stay significantly behind over time, usually confirmed after age 5. Children with intellectual disability, including those with Down syndrome, keep learning and growing throughout life, especially with early support.',
    ],
    notice: [
      'Milestones arriving noticeably later than for other children the same age, in more than one area.',
      'Needing more help than peers with feeding, dressing or toileting as they get older.',
      'Finding it harder to understand instructions, play pretend games, or keep up in play.',
    ],
    helps: [
      'Break everyday skills into tiny steps and practise one step at a time, many times, in the same order.',
      'Use routines (meals, bath, dressing) as learning time. They happen every day anyway.',
      'Show, then help, then step back: do it together, then let them do the last part themselves.',
      'Notice and celebrate small gains. Progress may be slower, but it is real.',
    ],
    myths: [
      { myth: 'Early help is pointless if they will be delayed anyway.', fact: 'Early support makes a real difference to independence and communication. It is never too early to start.' },
    ],
    when: [
      'Your child is behind in two or more areas, or has lost skills they had.',
      'Any concern at all under age 3. This is when support helps most.',
    ],
    who: ['Developmental paediatrician', 'Occupational therapist', 'Physiotherapist', 'Speech and language therapist', 'Special educator', 'Your nearest District Early Intervention Centre (DEIC)'],
    nurture:
      'The milestones check looks across movement, communication, play and independence for your child’s age. The report shows which areas are meeting milestones and which need attention, with activities for each room at home.',
    sources: [
      AAP('health-issues/conditions/developmental-disabilities/Pages/default.aspx', 'Developmental disabilities'),
      NHS('conditions/learning-disabilities/', 'Learning disabilities'),
      RBSK,
    ],
  },

  'sensory-differences': {
    intro: [
      'Children take in the world through their senses: sound, touch, taste, smell, sight, movement and body position. Some children feel certain sensations **much more strongly** than others (a tag in a shirt feels like sandpaper), and some **much less** (they crash, spin and seek strong pressure).',
      'Sensory differences are common in autistic children and in children with ADHD, but can also happen on their own.',
    ],
    notice: [
      'Covering ears at mixer-grinders, pressure cookers or crowds.',
      'Refusing certain clothes, hair-washing, nail-cutting, or foods with mixed textures.',
      'Constantly moving, jumping, spinning or chewing on things.',
      'Seeming not to notice when hurt, messy or called.',
      'Meltdowns in busy, noisy places like weddings, malls or markets.',
    ],
    helps: [
      'Notice patterns: what happened just before a hard moment? Plan around the triggers you find.',
      'Offer a calm corner with soft light and a cushion or blanket, and let them go there before things build up.',
      'Give movement breaks, jumping, carrying heavy bags, pushing a laundry basket, especially before tasks that need sitting.',
      'Warn before loud sounds, and let them wear ear defenders or headphones in noisy places.',
      'Introduce new textures and foods slowly, with no pressure.',
    ],
    when: ['Sensory reactions stop your child eating enough, sleeping, or joining everyday life.', 'Meltdowns are frequent or intense.'],
    who: ['Occupational therapist', 'Developmental paediatrician'],
    nurture:
      'Log hard moments with what came before them; patterns show up quickly. The home programme includes calm-corner and movement activities you can fit into the day.',
    sources: [AAP('health-issues/conditions/developmental-disabilities/Pages/Sensory-Integration-Therapy.aspx', 'Sensory integration')],
  },

  'coordination-difficulties': {
    intro: [
      'Developmental coordination disorder (DCD), sometimes called dyspraxia, means a child finds it much harder than others to plan and coordinate movements, from buttons and handwriting to catching a ball or riding a bicycle. It is not due to weakness or low intelligence.',
    ],
    notice: [
      'Seeming clumsy: bumping into things, falling often, spilling.',
      'Struggling with buttons, zips, laces, cutlery or scissors long after peers manage.',
      'Slow, tiring or hard-to-read handwriting.',
      'Avoiding sports and playground games; getting left out.',
    ],
    helps: [
      'Practise one skill at a time in short, fun sessions, a few minutes daily beats an hour once a week.',
      'Break skills into steps and practise the last step first so they finish with success.',
      'Choose clothes with elastic waists and Velcro while skills are building.',
      'Try activities that build strength and balance: swimming, climbing, carrying, playing catch with a big soft ball.',
    ],
    when: ['Coordination problems affect self-care, schoolwork or play, and aren’t improving.', 'Your child is losing confidence or avoiding activity.'],
    who: ['Occupational therapist', 'Physiotherapist', 'Developmental paediatrician'],
    nurture:
      'The home assessment’s milestones and independence questions cover everyday movement and self-care; the home programme has activities for hands and whole-body movement.',
    sources: [NHS('conditions/developmental-coordination-disorder-dyspraxia/', 'Developmental coordination disorder')],
  },

  anxiety: {
    intro: [
      'All children feel worried or scared sometimes, of the dark, of new places, of being away from you. Anxiety becomes a problem when worries are big, frequent, and stop a child doing things other children their age can do.',
      'Anxiety is very treatable, and parents are a big part of the help.',
    ],
    notice: [
      'Clinging, crying or tummy aches before school, outings or being left with others.',
      'Many “what if” questions, and needing lots of reassurance that doesn’t seem to last.',
      'Avoiding things they used to enjoy, or new situations.',
      'Trouble falling asleep, bad dreams, or wanting to sleep with you.',
      'Irritability or meltdowns that are really fear underneath.',
    ],
    helps: [
      'Name the feeling calmly: “You’re worried about tomorrow. That makes sense.”',
      'Help them face fears in small steps rather than avoiding them altogether; avoidance makes worry grow.',
      'Keep daily routines steady and sleep regular.',
      'Teach one calming skill when they are calm; slow “smell the flower, blow the candle” breathing.',
      'Notice your own worry; children learn how to handle fear from watching us.',
    ],
    when: [
      'Worry stops your child going to school, sleeping, eating or seeing friends.',
      'It has lasted more than a few weeks, or you are worried about their safety.',
    ],
    who: ['Child psychologist', 'Child psychiatrist', 'School counsellor'],
    nurture:
      'Teenage home assessments include questions about mood and worry, and the report says clearly when something needs attention. If your child ever talks about hurting themselves, call Tele-MANAS on **14416** straight away.',
    sources: [NHS('mental-health/children-and-young-adults/advice-for-parents/anxiety-in-children/', 'Anxiety in children'), AAP('health-issues/conditions/emotional-problems/Pages/Anxiety-Disorders.aspx', 'Anxiety disorders')],
  },
};
